import { useState } from 'react';

import type { SearchPerson } from '../data/graph-types.js';
import type { DeviceLibrarySnapshot, StoredTraceReference } from '../lib/device-library.js';

type SavedLibraryProps = {
  snapshot: DeviceLibrarySnapshot;
  peopleById: ReadonlyMap<string, SearchPerson>;
  onOpenPerson: (personId: string) => void;
  onOpenTrace: (trace: StoredTraceReference) => void;
  onRemoveFavoritePerson: (personId: string) => void;
  onRemoveFavoriteTrace: (trace: StoredTraceReference) => void;
  onRemoveRecentPerson: (personId: string) => void;
  onRemoveRecentTrace: (trace: StoredTraceReference) => void;
  onClearRecents: () => void;
  onClearAll: () => void;
};

function personName(peopleById: ReadonlyMap<string, SearchPerson>, personId: string): string {
  return peopleById.get(personId)?.name ?? personId;
}

function traceName(
  peopleById: ReadonlyMap<string, SearchPerson>,
  trace: StoredTraceReference,
): string {
  return `${personName(peopleById, trace.from)} → ${personName(peopleById, trace.to)}`;
}

function PersonItems({
  label,
  personIds,
  peopleById,
  onOpen,
  onRemove,
}: {
  label: string;
  personIds: readonly string[];
  peopleById: ReadonlyMap<string, SearchPerson>;
  onOpen: (personId: string) => void;
  onRemove: (personId: string) => void;
}) {
  if (personIds.length === 0) return null;
  return (
    <section className="saved-library__group" aria-labelledby={`${label}-people-heading`}>
      <h3 id={`${label}-people-heading`}>{label} people</h3>
      <ul>
        {personIds.map((personId) => {
          const name = personName(peopleById, personId);
          return (
            <li key={personId}>
              <button type="button" onClick={() => onOpen(personId)}>
                {name}
              </button>
              <button
                className="saved-library__remove"
                type="button"
                aria-label={`Remove ${name} from ${label.toLocaleLowerCase('en-US')} people`}
                onClick={() => onRemove(personId)}
              >
                Remove
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function TraceItems({
  label,
  traces,
  peopleById,
  onOpen,
  onRemove,
}: {
  label: string;
  traces: readonly StoredTraceReference[];
  peopleById: ReadonlyMap<string, SearchPerson>;
  onOpen: (trace: StoredTraceReference) => void;
  onRemove: (trace: StoredTraceReference) => void;
}) {
  if (traces.length === 0) return null;
  return (
    <section className="saved-library__group" aria-labelledby={`${label}-traces-heading`}>
      <h3 id={`${label}-traces-heading`}>{label} connections</h3>
      <ul>
        {traces.map((trace) => {
          const name = traceName(peopleById, trace);
          const key = [trace.from, trace.to, trace.storyId ?? '', ...(trace.lenses ?? [])].join(
            ':',
          );
          return (
            <li key={key}>
              <button type="button" onClick={() => onOpen(trace)}>
                <span>{name}</span>
                <small>
                  {trace.storyId === undefined ? 'Source-linked route' : 'Accepted featured story'}
                </small>
              </button>
              <button
                className="saved-library__remove"
                type="button"
                aria-label={`Remove ${name} from ${label.toLocaleLowerCase('en-US')} connections`}
                onClick={() => onRemove(trace)}
              >
                Remove
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function SavedLibrary({
  snapshot,
  peopleById,
  onOpenPerson,
  onOpenTrace,
  onRemoveFavoritePerson,
  onRemoveFavoriteTrace,
  onRemoveRecentPerson,
  onRemoveRecentTrace,
  onClearRecents,
  onClearAll,
}: SavedLibraryProps) {
  const [confirmClear, setConfirmClear] = useState(false);
  const state = snapshot.state;
  const favoriteCount = state.favoritePeople.length + state.favoriteTraces.length;
  const recentCount = state.recentPeople.length + state.recentTraces.length;
  const activityCount = favoriteCount + recentCount;

  return (
    <aside className="saved-library" aria-label="Device-local Saved library">
      <details>
        <summary>
          <span>Saved</span>
          <small>
            {activityCount.toLocaleString('en-US')}{' '}
            {activityCount === 1 ? 'item on this device' : 'items on this device'}
          </small>
        </summary>
        <div className="saved-library__body">
          <div className="saved-library__intro">
            <div>
              <p className="eyebrow">Private device library</p>
              <h2>Your people and connections</h2>
            </div>
            <a href="/challenge/">Open Daily connection</a>
          </div>
          <p>
            Favorites and recent activity stay only in this browser on this device. They are not
            synced or sent to Entangled Vega.
          </p>
          {snapshot.notice === null ? null : (
            <p className="notice" role="status">
              {snapshot.notice}
            </p>
          )}
          {activityCount === 0 ? (
            <p className="saved-library__empty">
              Nothing is saved yet. Select a person or complete a connection, then use its Save
              control.
            </p>
          ) : (
            <div className="saved-library__groups">
              <PersonItems
                label="Favorite"
                personIds={state.favoritePeople}
                peopleById={peopleById}
                onOpen={onOpenPerson}
                onRemove={onRemoveFavoritePerson}
              />
              <TraceItems
                label="Favorite"
                traces={state.favoriteTraces}
                peopleById={peopleById}
                onOpen={onOpenTrace}
                onRemove={onRemoveFavoriteTrace}
              />
              <PersonItems
                label="Recent"
                personIds={state.recentPeople}
                peopleById={peopleById}
                onOpen={onOpenPerson}
                onRemove={onRemoveRecentPerson}
              />
              <TraceItems
                label="Recent"
                traces={state.recentTraces}
                peopleById={peopleById}
                onOpen={onOpenTrace}
                onRemove={onRemoveRecentTrace}
              />
            </div>
          )}
          <div className="saved-library__controls">
            <button
              className="button button--small"
              type="button"
              disabled={recentCount === 0}
              onClick={onClearRecents}
            >
              Clear recent activity
            </button>
            {confirmClear ? (
              <div className="saved-library__confirmation" role="group" aria-label="Confirm clear">
                <p>Clear every favorite, recent item, and today’s challenge result?</p>
                <button
                  className="button button--small"
                  type="button"
                  onClick={() => {
                    onClearAll();
                    setConfirmClear(false);
                  }}
                >
                  Confirm clear all
                </button>
                <button
                  className="button button--small"
                  type="button"
                  onClick={() => setConfirmClear(false)}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                className="button button--small"
                type="button"
                disabled={activityCount === 0 && state.dailyChallenge === undefined}
                onClick={() => setConfirmClear(true)}
              >
                Clear all saved activity
              </button>
            )}
          </div>
          <a className="saved-library__privacy" href="/privacy">
            How device-local storage and aggregate analytics work
          </a>
        </div>
      </details>
    </aside>
  );
}
